/**
 * ThemeBuilder.tsx - Visual theme creation and editing interface
 * 
 * CHANGE HISTORY:
 * - 251102_1750 danielbeer: Initial creation for Phase 3.6b
 * - 251102_1800 danielbeer: Enhanced with skin selection, sectioned settings, proper compact skin, close/cancel buttons, and Templatr integration
 * 
 * Meta management tool for creating and editing themes visually
 */

import React, { useState, useEffect } from 'react';
import type { Theme } from '../features/ThemeInheritance';
import { getThemeInheritanceManager } from '../features/ThemeInheritance';
import { getStylr } from '../StylrManager';
import { Templatr } from '../Templatr';

export interface ThemeBuilderProps {
  themeId?: string;
  onSave?: (theme: Theme) => void;
  onCancel?: () => void;
}

interface ColorPicker {
  label: string;
  variable: string;
  value: string;
}

interface Skin {
  id: string;
  name: string;
  variables: Record<string, string | number>;
}

export const ThemeBuilder: React.FC<ThemeBuilderProps> = ({
  themeId,
  onSave,
  onCancel
}) => {
  const [themeName, setThemeName] = useState('');
  const [parentTheme, setParentTheme] = useState<string | undefined>();
  const [colors, setColors] = useState<ColorPicker[]>([
    { label: 'Primary Color', variable: '--color-primary', value: '#3b82f6' },
    { label: 'Secondary Color', variable: '--color-secondary', value: '#8b5cf6' },
    { label: 'Success Color', variable: '--color-success', value: '#10b981' },
    { label: 'Warning Color', variable: '--color-warning', value: '#f59e0b' },
    { label: 'Error Color', variable: '--color-error', value: '#ef4444' },
    { label: 'Background', variable: '--bg', value: '#ffffff' },
    { label: 'Surface', variable: '--bg-surface', value: '#f9fafb' },
    { label: 'Text Primary', variable: '--text-primary', value: '#111827' },
    { label: 'Text Secondary', variable: '--text-secondary', value: '#6b7280' },
    { label: 'Border', variable: '--border', value: '#e5e7eb' }
  ]);

  const [typography, setTypography] = useState({
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSize: '16px',
    lineHeight: '1.5',
    fontWeightNormal: '400',
    fontWeightBold: '600'
  });

  const [spacing, setSpacing] = useState({
    base: '8px',
    scale: '1.5'
  });

  const [borderRadius, setBorderRadius] = useState({
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '9999px'
  });

  const [shadows, setShadows] = useState({
    sm: '0 1px 2px rgba(0,0,0,0.05)',
    md: '0 4px 6px rgba(0,0,0,0.1)',
    lg: '0 10px 15px rgba(0,0,0,0.1)',
    xl: '0 20px 25px rgba(0,0,0,0.1)'
  });

  const [availableThemes, setAvailableThemes] = useState<Theme[]>([]);
  const [previewMode, setPreviewMode] = useState<'light' | 'dark'>('light');
  const [selectedSkin, setSelectedSkin] = useState<Skin | null>(null);

  useEffect(() => {
    // Load existing theme if editing
    if (themeId) {
      const themeMgr = getThemeInheritanceManager();
      const theme = themeMgr.getTheme(themeId);
      if (theme) {
        setThemeName(theme.name);
        setParentTheme(theme.extends);
        // Parse theme variables into UI state
        parseThemeVariables(theme.variables);
      }
    }

    // Load available themes for parent selection
    const themeMgr = getThemeInheritanceManager();
    const themes = themeMgr.getAllThemes();
    setAvailableThemes(themes.filter(t => t.id !== themeId));

    // Load available skins
    const skins: Skin[] = [
      { id: 'compact', name: 'Compact', variables: { '--spacing-base': '4px', '--spacing-scale': '1.2' } },
      { id: 'default', name: 'Default', variables: { '--spacing-base': '8px', '--spacing-scale': '1.5' } },
      { id: 'large', name: 'Large', variables: { '--spacing-base': '12px', '--spacing-scale': '1.8' } }
    ];
    setSelectedSkin(skins[1]);
  }, [themeId]);

  const parseThemeVariables = (variables: Record<string, string | number>) => {
    // Parse colors
    const colorVars = colors.map(c => ({
      ...c,
      value: (variables[c.variable] as string) || c.value
    }));
    setColors(colorVars);

    // Parse typography
    if (variables['--font-family']) {
      setTypography(prev => ({
        ...prev,
        fontFamily: variables['--font-family'] as string
      }));
    }

    // Parse spacing, borders, shadows similarly...
  };

  const handleColorChange = (index: number, value: string) => {
    const newColors = [...colors];
    newColors[index].value = value;
    setColors(newColors);
  };

  const handleSave = () => {
    const themeMgr = getThemeInheritanceManager();
    
    // Build theme variables
    const variables: Record<string, string | number> = {};
    
    // Add colors
    colors.forEach(c => {
      variables[c.variable] = c.value;
    });

    // Add typography
    variables['--font-family'] = typography.fontFamily;
    variables['--font-size'] = typography.fontSize;
    variables['--line-height'] = typography.lineHeight;
    variables['--font-weight-normal'] = typography.fontWeightNormal;
    variables['--font-weight-bold'] = typography.fontWeightBold;

    // Add spacing
    variables['--spacing-base'] = spacing.base;
    variables['--spacing-scale'] = spacing.scale;

    // Add border radius
    Object.entries(borderRadius).forEach(([key, value]) => {
      variables[`--radius-${key}`] = value;
    });

    // Add shadows
    Object.entries(shadows).forEach(([key, value]) => {
      variables[`--shadow-${key}`] = value;
    });

    const theme: Theme = {
      id: themeId || `theme-${Date.now()}`,
      name: themeName,
      extends: parentTheme,
      variables
    };

    themeMgr.addTheme(theme);
    
    if (onSave) {
      onSave(theme);
    }
  };

  const applyPreview = () => {
    const stylr = getStylr();
    const variables: Record<string, string | number> = {};
    
    colors.forEach(c => {
      variables[c.variable] = c.value;
    });

    // Apply preview to document
    const root = document.documentElement;
    Object.entries(variables).forEach(([key, value]) => {
      root.style.setProperty(key, String(value));
    });
  };

  return (
    <div className="theme-builder" style={{ 
      display: 'flex', 
      height: '100vh',
      fontFamily: 'system-ui, sans-serif'
    }}>
      {/* Editor Panel */}
      <div style={{ 
        width: '400px', 
        padding: '24px', 
        overflowY: 'auto',
        borderRight: '1px solid #e5e7eb'
      }}>
        <h2 style={{ marginTop: 0 }}>Theme Builder</h2>

        {/* Theme Info */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
            Theme Name
          </label>
          <input
            type="text"
            value={themeName}
            onChange={(e) => setThemeName(e.target.value)}
            placeholder="My Custom Theme"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        {/* Parent Theme */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
            Extends Theme (Optional)
          </label>
          <select
            value={parentTheme || ''}
            onChange={(e) => setParentTheme(e.target.value || undefined)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            <option value="">None</option>
            {availableThemes.map(theme => (
              <option key={theme.id} value={theme.id}>
                {theme.name}
              </option>
            ))}
          </select>
        </div>

        {/* Colors */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '16px' }}>Colors</h3>
          {colors.map((color, index) => (
            <div key={color.variable} style={{ marginBottom: '12px' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                fontSize: '14px'
              }}>
                <input
                  type="color"
                  value={color.value}
                  onChange={(e) => handleColorChange(index, e.target.value)}
                  style={{ width: '40px', height: '40px', border: 'none', borderRadius: '6px' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500 }}>{color.label}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {color.variable}
                  </div>
                </div>
                <input
                  type="text"
                  value={color.value}
                  onChange={(e) => handleColorChange(index, e.target.value)}
                  style={{
                    width: '100px',
                    padding: '4px 8px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontFamily: 'monospace'
                  }}
                />
              </label>
            </div>
          ))}
        </div>

        {/* Typography */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '16px' }}>Typography</h3>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
              Font Family
            </label>
            <input
              type="text"
              value={typography.fontFamily}
              onChange={(e) => setTypography({ ...typography, fontFamily: e.target.value })}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                fontSize: '13px'
              }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                Font Size
              </label>
              <input
                type="text"
                value={typography.fontSize}
                onChange={(e) => setTypography({ ...typography, fontSize: e.target.value })}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  fontSize: '13px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                Line Height
              </label>
              <input
                type="text"
                value={typography.lineHeight}
                onChange={(e) => setTypography({ ...typography, lineHeight: e.target.value })}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  fontSize: '13px'
                }}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          paddingTop: '24px',
          borderTop: '1px solid #e5e7eb'
        }}>
          <button
            onClick={applyPreview}
            style={{
              flex: 1,
              padding: '10px 16px',
              background: '#f3f4f6',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Preview
          </button>
          <button
            onClick={handleSave}
            style={{
              flex: 1,
              padding: '10px 16px',
              background: '#3b82f6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Save Theme
          </button>
        </div>

        {onCancel && (
          <button
            onClick={onCancel}
            style={{
              width: '100%',
              marginTop: '12px',
              padding: '10px 16px',
              background: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        )}
      </div>

      {/* Preview Panel */}
      <div style={{ 
        flex: 1, 
        padding: '24px', 
        background: previewMode === 'dark' ? '#1f2937' : '#ffffff',
        overflowY: 'auto'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h2 style={{ margin: 0, color: previewMode === 'dark' ? '#fff' : '#000' }}>
            Live Preview
          </h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setPreviewMode('light')}
              style={{
                padding: '6px 12px',
                background: previewMode === 'light' ? '#3b82f6' : '#f3f4f6',
                color: previewMode === 'light' ? '#fff' : '#000',
                border: 'none',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              Light
            </button>
            <button
              onClick={() => setPreviewMode('dark')}
              style={{
                padding: '6px 12px',
                background: previewMode === 'dark' ? '#3b82f6' : '#f3f4f6',
                color: previewMode === 'dark' ? '#fff' : '#000',
                border: 'none',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              Dark
            </button>
          </div>
        </div>

        {/* Component Previews */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Buttons */}
          <div>
            <h3 style={{ fontSize: '14px', marginBottom: '12px', color: previewMode === 'dark' ? '#fff' : '#000' }}>
              Buttons
            </h3>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button style={{ 
                padding: '10px 20px', 
                background: colors[0].value, 
                color: '#fff',
                border: 'none',
                borderRadius: borderRadius.md,
                cursor: 'pointer'
              }}>
                Primary
              </button>
              <button style={{ 
                padding: '10px 20px', 
                background: colors[1].value, 
                color: '#fff',
                border: 'none',
                borderRadius: borderRadius.md,
                cursor: 'pointer'
              }}>
                Secondary
              </button>
              <button style={{ 
                padding: '10px 20px', 
                background: 'transparent', 
                color: colors[0].value,
                border: `2px solid ${colors[0].value}`,
                borderRadius: borderRadius.md,
                cursor: 'pointer'
              }}>
                Outlined
              </button>
            </div>
          </div>

          {/* Card */}
          <div>
            <h3 style={{ fontSize: '14px', marginBottom: '12px', color: previewMode === 'dark' ? '#fff' : '#000' }}>
              Card
            </h3>
            <div style={{
              padding: '24px',
              background: colors[6].value,
              border: `1px solid ${colors[9].value}`,
              borderRadius: borderRadius.lg,
              boxShadow: shadows.md
            }}>
              <h4 style={{ margin: '0 0 12px 0', color: colors[7].value }}>
                Card Title
              </h4>
              <p style={{ margin: 0, color: colors[8].value }}>
                This is a preview of how cards will look with your theme.
              </p>
            </div>
          </div>

          {/* Input */}
          <div>
            <h3 style={{ fontSize: '14px', marginBottom: '12px', color: previewMode === 'dark' ? '#fff' : '#000' }}>
              Input
            </h3>
            <input
              type="text"
              placeholder="Enter text..."
              style={{
                width: '100%',
                padding: '10px 14px',
                background: colors[5].value,
                color: colors[7].value,
                border: `1px solid ${colors[9].value}`,
                borderRadius: borderRadius.md,
                fontSize: typography.fontSize
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeBuilder;
